<template>
	<v-dialog
		v-model="visible"
		fullscreen
		persistent
		content-class="clip-video-fullscreen-dialog"
		transition="dialog-bottom-transition"
		@keydown="onKeydown">
		<v-card class="h-100 d-flex flex-column">
			<!-- Header -->
			<v-card-title
				class="dialog-header d-flex align-center flex-shrink-0"
				:class="smAndDown ? 'pa-2 px-3' : 'pa-4'">
				<h5 v-if="!smAndDown" id="dialog-title" class="text-h5 flex-grow-0">Edit Video</h5>
				<h6 v-else id="dialog-title-mobile" class="text-h6 flex-grow-0">Edit Video</h6>
				<v-spacer />
				<v-btn
					variant="text"
					rounded="pill"
					:size="smAndDown ? 'small' : 'default'"
					class="mr-2"
					@click="onCancel">
					Cancel
				</v-btn>
				<PrimaryBtn
					:size="smAndDown ? 'small' : 'default'"
					:disabled="isSaving"
					:loading="isSaving"
					@click="onSave">
					Save
				</PrimaryBtn>
			</v-card-title>

			<!-- Body: full height two-panel layout -->
		<v-card-text
			class="clip-video-body d-flex flex-column flex-grow-1 overflow-hidden"
			:class="smAndDown ? 'pa-2' : 'pa-4'">
				<div
					class="clip-video-body-inner d-flex"
					:class="mdAndDown ? 'flex-column ga-3 overflow-y-auto overflow-x-hidden' : 'flex-row ga-4'">
					<!-- Left panel: Video + Timeline + Controls -->
					<div class="clip-video-left d-flex flex-column flex-grow-1">
						<div class="d-flex flex-column flex-grow-1" :class="smAndDown ? 'ga-2' : 'ga-3'">
						<!-- Video Player -->
						<div
							class="clip-video-player flex-grow-1 position-relative overflow-hidden rounded-lg"
							:class="mdAndDown ? 'clip-video-player--compact' : ''">
							<VideoPlayer
								ref="videoPlayer"
								:controls-enabled="false"
								:recording-id="recordingId"
								:source="videoSource"
								@loaded-metadata="onLoadedMetadata" />

							<!-- Buffering overlay (shown when video hasn't loaded data at the current timestamp) -->
							<v-overlay
								:model-value="videoBuffering"
								contained
								persistent
								class="align-center justify-center"
								transition="fade-transition">
								<v-progress-circular indeterminate size="48" width="4" color="white" />
							</v-overlay>
						</div>

						<!-- Timeline -->
						<ClipTimeline
								:duration="videoDuration"
								:video-fps="resolvedVideoFps"
								:clips="clips"
								:playhead-time="playheadTime"
								:drag-state="dragState"
								:active-clip-id="activeClipId"
								:visible-start="visibleStart"
								:visible-end="visibleEnd"
								:is-zoomed="isZoomed"
								:scroll-model="scrollPosition"
								:compact="smAndDown"
								@seek="onSeek"
								@drag-start="startDrag"
								@drag-move="updateDrag"
								@drag-end="onDragEnd"
								@clip-click="onClipClick"
								@scroll="onScroll" />

							<!-- Play controls + time + mute -->
							<div class="d-flex align-center flex-shrink-0" :class="smAndDown ? 'ga-1' : 'ga-3'">
								<v-btn icon variant="text" :density="smAndDown ? 'compact' : 'comfortable'" @click="onTogglePlayPause">
									<v-icon :size="smAndDown ? 'small' : 'default'">{{ isPlaying ? "icon-pause" : "icon-play-solid" }}</v-icon>
									<v-tooltip activator="parent" location="top">{{ isPlaying ? "Pause (Space)" : "Play (Space)" }}</v-tooltip>
								</v-btn>
								<span :class="smAndDown ? 'text-caption' : 'text-body-2'" class="text-medium-emphasis text-no-wrap">
									{{ formattedPlayheadTime }} / {{ formattedDuration }}
								</span>
							<v-spacer />
							<template v-if="maxZoom > 0">
								<span class="d-inline-flex">
									<v-btn
										icon
										variant="text"
										density="compact"
										:disabled="zoomLevel === 0"
										@click="zoomOut">
										<v-icon size="small" class="text-medium-emphasis">icon-zoom-out</v-icon>
									</v-btn>
									<v-tooltip activator="parent" location="top">Zoom out timeline</v-tooltip>
								</span>
								<v-slider
									v-model="zoomLevel"
									:min="0"
									:max="maxZoom"
									:step="1"
									hide-details
									density="compact"
									color="primary"
									thumb-size="14"
									:class="smAndDown ? 'zoom-slider--compact' : 'zoom-slider'" />
								<span class="d-inline-flex">
									<v-btn
										icon
										variant="text"
										density="compact"
										:disabled="zoomLevel === maxZoom"
										@click="zoomIn">
										<v-icon size="small" class="text-medium-emphasis">icon-zoom-in</v-icon>
									</v-btn>
									<v-tooltip activator="parent" location="top">Zoom in timeline</v-tooltip>
								</span>
							</template>
							</div>
						</div>
					</div>

					<!-- Right panel: Clip List (fixed width on desktop, full width on mobile/tablet) -->
					<div
						class="clip-video-right d-flex flex-column flex-shrink-0 overflow-hidden"
						:class="mdAndDown ? 'clip-video-right--stacked' : ''">
						<ClipList
							:clips="clips"
							:total-duration="totalClipDuration"
							:preview-mode="previewMode"
							:active-clip-id="activeClipId"
							@remove-clip="removeClip"
							@preview-all="onPreviewAll"
							@stop-preview="stopPreview"
							@clip-click="onClipClick" />
					</div>
				</div>
			</v-card-text>
		</v-card>
	</v-dialog>
</template>

<script setup lang="ts">
	import { ref, computed, watch, onBeforeUnmount } from "vue";
	import { useDisplay } from "vuetify";
	import VideoPlayer from "@/components/VideoPlayer.vue";
	import ClipTimeline from "./ClipTimeline.vue";
	import ClipList from "./ClipList.vue";
	import { useClipVideo } from "@/composables/useClipVideo";
	import { formatSeconds } from "@/helpers/time";
	import { DEFAULT_VIDEO_FPS, resolveFpsFromS3Url, snapTimeToFrame } from "@/helpers/videoFps";
	import { useToast } from "vue-toastification";

	const { smAndDown, mdAndDown } = useDisplay();

	const visible = defineModel<boolean>("visible", { required: true, default: false });

	const props = defineProps<{
		videoSource: string;
		recordingId: string;
		conversationId?: string;
		projectId?: string;
	}>();

	const emit = defineEmits<{
		(e: "saved", jobId: string): void;
		(e: "cancel"): void;
	}>();

	const toast = useToast();

	const {
		clips,
		videoDuration,
		playheadTime,
		isPlaying,
		activeClipId,
		isSaving,
		dragState,
		previewMode,
		totalClipDuration,
		setVideoElement,
		setVideoDuration,
		setVideoFps,
		removeClip,
		seekTo,
		togglePlayPause,
		startDrag,
		updateDrag,
		commitDrag,
		previewAllClips,
		stopPreview,
		save,
		reset
	} = useClipVideo();

	const videoBuffering = ref(false);
	const resolvedVideoFps = ref(DEFAULT_VIDEO_FPS);
	let videoEl: HTMLVideoElement | null = null;
	let timeUpdateAnimationId: number | null = null;
	let bufferingTimerId: ReturnType<typeof setTimeout> | null = null;

	const formattedPlayheadTime = computed(() => formatSeconds(playheadTime.value));
	const formattedDuration = computed(() => formatSeconds(videoDuration.value));

	// --- Zoom + Scroll state ---
	const MIN_VISIBLE_WINDOW = 120; // 2 minutes in seconds
	const ZOOM_STEP = 10;
	const zoomLevel = ref(0);
	const scrollPosition = ref(0);
	const userScrolled = ref(false);

	const maxZoom = computed(() => (videoDuration.value <= MIN_VISIBLE_WINDOW ? 0 : 100));
	const isZoomed = computed(() => zoomLevel.value > 0);

	const visibleDuration = computed(() => {
		if (!isZoomed.value) return videoDuration.value;
		return Math.max(MIN_VISIBLE_WINDOW, videoDuration.value * (1 - zoomLevel.value / 100));
	});

	const maxScroll = computed(() => Math.max(0, videoDuration.value - visibleDuration.value));

	const visibleStart = computed(() => {
		if (!isZoomed.value) return 0;
		return (scrollPosition.value / 100) * maxScroll.value;
	});

	const visibleEnd = computed(() => {
		if (!isZoomed.value) return videoDuration.value;
		return visibleStart.value + visibleDuration.value;
	});

	function onScroll(position: number) {
		scrollPosition.value = position;
		userScrolled.value = true;
	}

	function zoomIn() {
		zoomLevel.value = Math.min(zoomLevel.value + ZOOM_STEP, maxZoom.value);
	}

	function zoomOut() {
		zoomLevel.value = Math.max(zoomLevel.value - ZOOM_STEP, 0);
	}

	// Auto-follow: keep playhead in view during playback
	watch(playheadTime, (time) => {
		if (!isZoomed.value || !isPlaying.value || userScrolled.value) return;
		if (time < visibleStart.value || time > visibleEnd.value) {
			const targetCenter = time;
			const halfWindow = visibleDuration.value / 2;
			const targetStart = Math.max(0, Math.min(targetCenter - halfWindow, maxScroll.value));
			scrollPosition.value = maxScroll.value > 0 ? (targetStart / maxScroll.value) * 100 : 0;
		}
	});

	// When zoom level changes, keep visibleStart anchored (left edge stays at same time).
	// We must reconstruct the old visibleStart from the old zoom level because by the
	// time this watcher fires, the computed `visibleStart` already uses the new maxScroll.
	watch(zoomLevel, (_newZoom, oldZoom) => {
		if (maxScroll.value <= 0) {
			scrollPosition.value = 0;
			return;
		}

		// Reconstruct old visibleStart from old zoom level + current scrollPosition
		const oldVisDuration =
			oldZoom > 0
				? Math.max(MIN_VISIBLE_WINDOW, videoDuration.value * (1 - oldZoom / 100))
				: videoDuration.value;
		const oldMaxScroll = Math.max(0, videoDuration.value - oldVisDuration);
		const oldVisibleStart = oldZoom > 0 ? (scrollPosition.value / 100) * oldMaxScroll : 0;

		const clampedStart = Math.min(oldVisibleStart, maxScroll.value);
		scrollPosition.value = (clampedStart / maxScroll.value) * 100;
	});

	function onLoadedMetadata(videoElement: HTMLVideoElement) {
		videoEl = videoElement;
		setVideoElement(videoElement);
		setVideoDuration(videoElement.duration);
		startTimeSync();
		attachBufferingListeners(videoElement);
	}

	async function resolveVideoFps() {
		const metadataFps = await resolveFpsFromS3Url(props.videoSource);
		resolvedVideoFps.value = metadataFps ?? DEFAULT_VIDEO_FPS;
		setVideoFps(resolvedVideoFps.value);
	}

	// --- Buffering / seeking detection ---
	// Show a loading spinner when the video hasn't loaded data at the current
	// timestamp (e.g. scrubbing to an uncached position). A short delay avoids
	// flashing the spinner on quick, already-cached seeks.

	function showBuffering() {
		if (bufferingTimerId !== null) return; // already scheduled
		bufferingTimerId = setTimeout(() => {
			videoBuffering.value = true;
			bufferingTimerId = null;
		}, 300);
	}

	function hideBuffering() {
		if (bufferingTimerId !== null) {
			clearTimeout(bufferingTimerId);
			bufferingTimerId = null;
		}
		videoBuffering.value = false;
	}

	function onVideoSeeking() {
		showBuffering();
	}
	function onVideoSeeked() {
		hideBuffering();
	}
	function onVideoWaiting() {
		showBuffering();
	}
	function onVideoCanPlay() {
		hideBuffering();
	}
	function onVideoPlaying() {
		hideBuffering();
	}

	function attachBufferingListeners(el: HTMLVideoElement) {
		el.addEventListener("seeking", onVideoSeeking);
		el.addEventListener("seeked", onVideoSeeked);
		el.addEventListener("waiting", onVideoWaiting);
		el.addEventListener("canplay", onVideoCanPlay);
		el.addEventListener("playing", onVideoPlaying);
	}

	function detachBufferingListeners(el: HTMLVideoElement) {
		el.removeEventListener("seeking", onVideoSeeking);
		el.removeEventListener("seeked", onVideoSeeked);
		el.removeEventListener("waiting", onVideoWaiting);
		el.removeEventListener("canplay", onVideoCanPlay);
		el.removeEventListener("playing", onVideoPlaying);
	}

	function startTimeSync() {
		stopTimeSync();
		function syncTime() {
			if (videoEl) {
				// During preview mode the preview loop in useClipVideo owns
				// playheadTime updates (it also handles seeking between clips).
				// Writing here would overwrite its value with a stale
				// currentTime before the seek completes, causing the playhead
				// to visually jump out of sync on mobile.
				if (!previewMode.value) {
					const time = videoEl.currentTime;
					// While playing: use raw time for smooth animation.
					// While paused: snap to nearest frame boundary so the
					// playhead rests on an exact frame position.
					playheadTime.value =
						!videoEl.paused && !videoEl.ended
							? time
							: snapTimeToFrame(time, resolvedVideoFps.value);
				}
				isPlaying.value = !videoEl.paused && !videoEl.ended;
			}
			timeUpdateAnimationId = requestAnimationFrame(syncTime);
		}
		timeUpdateAnimationId = requestAnimationFrame(syncTime);
	}

	function stopTimeSync() {
		if (timeUpdateAnimationId !== null) {
			cancelAnimationFrame(timeUpdateAnimationId);
			timeUpdateAnimationId = null;
		}
	}

	function onSeek(time: number) {
		seekTo(time);
	}

	function onTogglePlayPause() {
		userScrolled.value = false;
		togglePlayPause();
	}

	const MIN_INTENTIONAL_DRAG_PX = 5;

	function onDragEnd(startX: number, endX: number) {
		const result = commitDrag();
		const distance = Math.abs(endX - startX);
		if (!result.created && result.reason === "too_short" && distance > MIN_INTENTIONAL_DRAG_PX) {
			toast.info("Each piece must be at least 1 second long.");
		}
	}

	function onClipClick(clipId: string) {
		activeClipId.value = clipId;
		const clip = clips.value.find((c) => c.id === clipId);
		if (clip) {
			seekTo(clip.startTime);
		}
	}

	function onPreviewAll() {
		if (clips.value.length === 0) {
			toast.info("Choose at least one piece before previewing the final video.");
			return;
		}
		previewAllClips();
	}

	async function onSave() {
		if (clips.value.length === 0) {
			toast.info("Choose at least one piece before saving the final video.");
			return;
		}
		try {
			const response = await save(props.recordingId, props.conversationId, props.projectId);
			toast.success("Save successful. It will take a few minutes to see your updates.");
			emit("saved", response.jobId);
			visible.value = false;
		} catch (error) {
			console.error("Failed to save clip merge job", error as Error);
			const message = (error as Error).message || "An unexpected error occurred.";
			toast.error(`Failed to save clip: ${message}`);
		}
	}

	function onCancel() {
		stopPreview();
		emit("cancel");
		visible.value = false;
	}

	function onKeydown(event: KeyboardEvent) {
		if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
			return;
		}

		switch (event.code) {
			case "Space":
				event.preventDefault();
				onTogglePlayPause();
				break;
			case "Escape":
				event.preventDefault();
				if (clips.value.length > 0) {
					clips.value = [];
					activeClipId.value = null;
					stopPreview();
				}
				break;
		}
	}

	watch(
		visible,
		(newVal) => {
			if (newVal) {
				void resolveVideoFps();
			} else {
				stopTimeSync();
				if (videoEl) detachBufferingListeners(videoEl);
				hideBuffering();
				reset();
				videoEl = null;
				zoomLevel.value = 0;
				scrollPosition.value = 0;
				userScrolled.value = false;
				resolvedVideoFps.value = DEFAULT_VIDEO_FPS;
			}
		}
	);

	onBeforeUnmount(() => {
		stopTimeSync();
		if (videoEl) detachBufferingListeners(videoEl);
		hideBuffering();
		reset();
	});
</script>

<style scoped>
.clip-video-body-inner {
	flex: 1;
	overflow: hidden;
}

/*
 * LSP-19249: this component deliberately has no min-height chain. A `min-h-0`
 * class used to sit on these flex items, but Vuetify ships no such utility, so it
 * resolved to nothing. Setting the real property here collapses the left panel to
 * zero height in the stacked (md-and-down) layout: the body-inner scrolls there, so
 * a newly shrinkable left panel loses its height to its siblings and the video
 * disappears. The clip list gets its scrollable height from its own min-height
 * chain inside ClipList.vue instead.
 *
 * Why the stacked body-inner scrolls even though the rule right above sets
 * `overflow: hidden`: the template adds Vuetify's `overflow-y-auto` class in that
 * layout, and Vuetify generates every utility with `!important`, which beats a
 * non-`!important` declaration regardless of specificity — so the utility wins even
 * though Vue compiles the rule above to the higher-specificity
 * `.clip-video-body-inner[data-v-…]`. That also gives the element an automatic
 * minimum size of zero (a flex item whose overflow is not `visible` may shrink
 * below its content), which is why the chain is unnecessary here in the first
 * place. Pinned by the cascade-fidelity test in
 * automation/contactaiautomation/tests/lsp-19249-clip-list-scroll.layout.breakit.spec.ts.
 */
.clip-video-left {
	min-width: 0;
}

.clip-video-right {
	width: 360px;
	min-width: 320px;
}

/* Stacked mode (tablet / mobile): full-width clip list */
.clip-video-right--stacked {
	width: 100%;
	min-width: 0;
}

.clip-video-player {
	background-color: rgb(var(--v-theme-on-surface));
	min-height: 0;
	aspect-ratio: 16 / 9;
	flex-grow: 0 !important;
	width: 100%;
	max-height: calc(100vh - 270px);
}

/* Ensure the video inside maintains aspect ratio and is never cropped */
.clip-video-player :deep(video) {
	object-fit: contain !important;
}

/* Cap video height when stacked so it doesn't push the clip list off-screen */
.clip-video-player--compact {
	max-height: 45vh;
}

.zoom-slider {
	max-width: 120px;
}

.zoom-slider--compact {
	max-width: 80px;
}
</style>
